import OneAIBrandNetwork from '../components/OneAIBrandNetwork';
import { useCounter } from '../hooks/useCounter';

// Animated Stat Component for About page
const AnimatedStat = ({ value, label, color }: { 
  value: string; 
  label: string; 
  color: string 
}) => {
  // Handle different formats: "10x", "99.9%", "24/7"
  let numericValue = 0
  let displayValue = value

  if (value.includes('/')) {
    // Handle "24/7" format
    const nums = value.split('/')
    numericValue = parseInt(nums[0])
    const { count, ref } = useCounter({ end: numericValue, duration: 2000, startOnView: true })
    displayValue = `${count}/${nums[1]}`
    
    return (
      <div ref={ref} className="text-center">
        <div className={`text-4xl font-bold ${color} mb-2`}>{displayValue}</div>
        <div className="text-gray-600 font-medium">{label}</div>
      </div>
    )
  } else if (value.includes('%')) {
    // Handle "99.9%" format - animate to 99, then add .9%
    numericValue = 99
    const { count, ref } = useCounter({ end: numericValue, duration: 2000, startOnView: true })
    displayValue = `${count}.9%`
    
    return (
      <div ref={ref} className="text-center">
        <div className={`text-4xl font-bold ${color} mb-2`}>{displayValue}</div>
        <div className="text-gray-600 font-medium">{label}</div>
      </div>
    )
  } else if (value.includes('x')) {
    // Handle "10x" format
    numericValue = parseInt(value.replace('x', ''))
    const { count, ref } = useCounter({ end: numericValue, duration: 2000, startOnView: true })
    displayValue = `${count}x`
    
    return (
      <div ref={ref} className="text-center">
        <div className={`text-4xl font-bold ${color} mb-2`}>{displayValue}</div>
        <div className="text-gray-600 font-medium">{label}</div>
      </div>
    )
  }

  // Fallback for other formats
  return (
    <div className="text-center">
      <div className={`text-4xl font-bold ${color} mb-2`}>{value}</div>
      <div className="text-gray-600 font-medium">{label}</div>
    </div>
  )
}

const About = () => {
  return (
    <div className="about-page">
      {/* Hero Section */}
      <section 
        className="relative min-h-screen flex items-center overflow-hidden bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)), url('/assets/testbackgroundoneai4.png')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}
      >
        {/* Background Elements */}
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-0 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-1/4 right-0 w-80 h-80 bg-purple-500/10 rounded-full blur-3xl"></div>
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center px-4 py-2 bg-blue-600/80 text-white rounded-full text-sm font-semibold mb-8 backdrop-blur-sm">
              About oneAI
            </div>
            
            <h1 className="text-5xl lg:text-7xl font-bold text-white leading-tight mb-6 drop-shadow-lg">
              Making Systems{' '}
              <span className="block">
                <span className="bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">
                  Smarter. Faster. Better.
                </span>
              </span>
            </h1>
            
            <p className="text-xl lg:text-2xl text-gray-200 leading-relaxed mb-8 max-w-3xl mx-auto drop-shadow-sm">
              We transform outdated business systems with intelligent AI agents and consulting. 
              Four industry veterans dedicated to moving companies beyond legacy limitations—delivering 
              solutions that scale, comply, and perform from day one.
            </p>
            
            <a 
              href="/contact" 
              className="inline-flex items-center px-8 py-4 bg-blue-600 text-white text-lg font-semibold rounded-lg hover:bg-blue-700 transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
            >
              <span className="mr-3">Get Started</span>
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14m-7-7l7 7-7 7" />
              </svg>
            </a>
          </div>
        </div>
      </section>

      {/* Manifesto Section */}
      <section className="py-20 bg-white relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-30">
          <svg className="w-full h-full" viewBox="0 0 100 100">
            <defs>
              <pattern id="grid" width="20" height="20" patternUnits="userSpaceOnUse">
                <path d="M 20 0 L 0 0 0 20" fill="none" stroke="rgba(99,102,241,0.06)" strokeWidth="0.5"/>
              </pattern>
            </defs>
            <rect width="100" height="100" fill="url(#grid)"/>
          </svg>
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto">
            <div className="bg-white/95 backdrop-blur-lg rounded-2xl p-8 lg:p-12 shadow-2xl border border-white/20">
              {/* Quote Icon */}
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-8 mx-auto">
                <svg className="w-8 h-8 text-blue-600" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h4v10h-10z"/>
                </svg>
              </div>

              {/* Quote Text */}
              <blockquote className="text-2xl lg:text-3xl font-medium text-gray-800 text-center leading-relaxed mb-12">
                oneAI is your platform for developing, integrating, and scaling business ready AI agents plus 
                hands on consulting when you need it.
              </blockquote>

              {/* Stats */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                <AnimatedStat
                  value="10x"
                  label="Workflow Speed"
                  color="text-blue-600"
                />
                <AnimatedStat
                  value="99.9%"
                  label="Data On-Site"
                  color="text-purple-600"
                />
                <AnimatedStat
                  value="24/7"
                  label="AI Operations"
                  color="text-indigo-600"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Story Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
              {/* Left Column - Content */}
              <div>
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 md:mb-6">
                  Our Story
                </h2>
                <div className="space-y-4 md:space-y-6 text-gray-600 leading-relaxed text-sm md:text-base">
                  <p>
                    Founded by four industry veterans with decades of combined experience in AI, automation, 
                    and enterprise software, oneAI was born from a simple observation: businesses are drowning 
                    in inefficient processes while powerful AI solutions remain out of reach.
                  </p>
                  <p>
                    We witnessed countless companies struggling with legacy systems, manual workflows, and 
                    the growing complexity of modern business operations. Traditional SaaS solutions, while 
                    helpful, still require constant human intervention and can't adapt to changing business needs.
                  </p>
                  <p>
                    That's when we realized the future wasn't about better software—it was about intelligent 
                    agents that could think, learn, and act autonomously. We founded oneAI to make this 
                    future accessible to every business, regardless of size or technical expertise.
                  </p>
                </div>
              </div>

              {/* Right Column - OneAI Brand Network */}
              <div className="relative flex justify-center items-center">
                {/* Floating geometric elements */}
                <div className="absolute -top-2 -left-2 md:-top-4 md:-left-4 w-16 h-16 md:w-24 md:h-24 bg-blue-500/10 rounded-full blur-xl"></div>
                <div className="absolute -bottom-2 -right-2 md:-bottom-4 md:-right-4 w-20 h-20 md:w-32 md:h-32 bg-purple-500/10 rounded-full blur-xl"></div>
                
                <div className="bg-gradient-to-br from-blue-50 to-purple-50 p-4 md:p-6 lg:p-8 rounded-2xl relative z-10 border border-white/50 shadow-xl backdrop-blur-sm w-full max-w-sm md:max-w-md lg:max-w-none">
                  <div className="flex justify-center scale-75 md:scale-90 lg:scale-100">
                    <OneAIBrandNetwork />
                  </div>
                  
                  <div className="text-center mt-4 md:mt-6">
                    <h4 className="text-base md:text-lg font-semibold text-gray-800 mb-1 md:mb-2">
                      Seamless Integrations
                    </h4>
                    <p className="text-xs md:text-sm text-gray-600">
                      oneAI connects with the platforms you already use
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-6">Our Values</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                These principles guide everything we do and shape how we approach every project.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="bg-white/95 backdrop-blur-sm p-8 rounded-2xl shadow-lg text-center border border-white/50">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Innovation First</h3>
                <p className="text-gray-600">
                  We push the boundaries of what's possible with AI, always staying ahead of the curve 
                  and bringing cutting-edge solutions to our clients.
                </p>
              </div>

              <div className="bg-white/95 backdrop-blur-sm p-8 rounded-2xl shadow-lg text-center border border-white/50">
                <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <svg className="w-8 h-8 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Client Success</h3>
                <p className="text-gray-600">
                  Your success is our success. We're committed to delivering measurable results and 
                  building long-term partnerships based on trust and excellence.
                </p>
              </div>

              <div className="bg-white/95 backdrop-blur-sm p-8 rounded-2xl shadow-lg text-center border border-white/50">
                <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <svg className="w-8 h-8 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Future Ready</h3>
                <p className="text-gray-600">
                  We build solutions that scale and evolve with your business needs, ensuring you're 
                  always prepared for what's next in the rapidly changing world of AI.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-purple-600">
        <div className="container mx-auto px-4 text-center">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6">
              Ready to take the reins?
            </h2>
            <p className="text-xl text-blue-100 mb-8">
              Let's discuss how we can transform your business with intelligent AI agents.
            </p>
            <a 
              href="/contact" 
              className="inline-flex items-center px-8 py-4 bg-white text-blue-600 font-semibold rounded-lg hover:bg-gray-100 transition-all duration-300 transform hover:scale-105 shadow-lg"
            >
              Contact Us
              <svg className="w-5 h-5 ml-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14m-7-7l7 7-7 7" />
              </svg>
            </a>
          </div>
        </div>
      </section>
    </div>
  )
}

export default About
